// Platform-default appearance catalog. Named colour schemes live here as
// data — the Settings UI iterates this list instead of a hardcoded union.
// Master lists them as the built-in appearances (I-2510) and knows them by id
// only: a look names its scheme in `source_scheme`, Connect resolves the id
// against this catalog (seeds, pinned tokens, type, axes).
import { defaultSeeds, brandThemes } from '../seeds/defaultSeeds';
import { NAVY_MANUAL_SEED, manualBrandThemes } from '../seeds/navyManual';
import {
  GALLERY_THEMES,
  galleryRoleOverrides,
  gallerySharedOverrides,
  galleryType,
  type GalleryTheme,
} from '../seeds/galleryThemes';
import type { RuntimeBrandSeeds } from '../runtime/emitRuntimeBrandCss';
import type { RuntimeTypeConfig } from '../runtime/emitRuntimeTypeCss';
import type { AppearanceDesignAxes } from './layouts';
import type { SeedConfig, TokenMap } from '../generator/types';

export const PLATFORM_APPEARANCE_SCHEME_IDS = [
  'navy',
  'navy-manual',
  'petrol',
  'pink',
  'amber',
  'blp-ci',
  'signal-red',
  'signal-yellow',
  'northern-lights',
  'editorial',
  'graphite-amber',
  'pastel',
  'terminal',
  'nordic',
  'steel-orange',
  'workbench',
  'workbench-light',
  'workbench-calm',
] as const;

export type NamedBrand = (typeof PLATFORM_APPEARANCE_SCHEME_IDS)[number];

export const PLATFORM_APPEARANCE_DEFAULT_SCHEME: NamedBrand = 'navy';

export const CUSTOM_APPEARANCE_SCHEME_ID = 'custom';
export const CUSTOM_APPEARANCE_LABEL_KEY = 'connect.settings.appearance.brandCustom';

export type AppearanceScheme = {
  id: NamedBrand;
  labelKey: string;
  seeds: RuntimeBrandSeeds;
  /** Hand-curated role maps. When set, apply these instead of ramping seeds. */
  tokens?: { light: TokenMap; dark: TokenMap };
  /** Sparse role overrides on top of the seed ramp (or `tokens`), per mode. */
  overrides?: { light?: TokenMap; dark?: TokenMap };
  /** Mode-independent overrides (radius, font sizes) — a scheme with these is a complete look. */
  shared?: TokenMap;
  /** Typography of the scheme; without it the scheme uses the appearance base. */
  type?: RuntimeTypeConfig;
  /** Design axes the scheme sets; the rest come from the appearance base. */
  design?: AppearanceDesignAxes;
};

function statusSeeds(): Pick<RuntimeBrandSeeds, 'danger' | 'success' | 'warning'> {
  return {
    danger: defaultSeeds.roles.danger,
    success: defaultSeeds.roles.success,
    warning: defaultSeeds.roles.warning,
  };
}

function completeRuntimeSeeds(overrides: Partial<SeedConfig['roles']>): RuntimeBrandSeeds {
  return {
    brand: overrides.brand ?? defaultSeeds.roles.brand,
    neutral: overrides.neutral ?? defaultSeeds.roles.neutral,
    ...(overrides.info ? { info: overrides.info } : {}),
    ...(overrides.danger ? { danger: overrides.danger } : {}),
    ...(overrides.success ? { success: overrides.success } : {}),
    ...(overrides.warning ? { warning: overrides.warning } : {}),
  };
}

/**
 * Petrol (I-2510): the offer mockup V10-25 as a design, in ONE blue. The brand
 * is the real navy seed; in light its fill, the top bar and the link colour
 * (group titles, the active section's bar, links, aside icons) are all pinned
 * to it — the ramp normalises lightness, so its own steps would be other
 * navies, and the mockup's second, lighter accent blue is left out on purpose
 * (dark lifts the ramp like any, the brand must stay readable there). A slate
 * neutral. System type at the default size; hairline surfaces on a page of one
 * sheet, compact spacing, plain aside lists, pill badges.
 * Its form, table and ERP settings are master's built-in layers
 * (`backend/master/accountconfig/builtin.go`) — a scheme carries the look only.
 */
const PETROL_SEEDS = { brand: NAVY_MANUAL_SEED, neutral: '#566b7c' } as const;

function lowerHex<T extends Record<string, string | undefined>>(seeds: T): T {
  return Object.fromEntries(
    Object.entries(seeds).map(([key, hex]) => [key, hex?.toLowerCase()])
  ) as T;
}

function galleryScheme(theme: GalleryTheme): AppearanceScheme {
  const shared = gallerySharedOverrides(theme);
  return {
    id: theme.id as NamedBrand,
    labelKey: theme.labelKey,
    seeds: completeRuntimeSeeds({
      ...statusSeeds(),
      ...lowerHex(theme.status),
      brand: theme.colors.accent.toLowerCase(),
      neutral: theme.colors.muted.toLowerCase(),
    }),
    overrides: { [theme.mode]: galleryRoleOverrides(theme) },
    ...(Object.keys(shared).length ? { shared } : {}),
    type: galleryType(theme),
    // The gallery looks are drawn tight; compact spacing is part of them.
    design: { density: 'compact' },
  };
}

export const PLATFORM_APPEARANCE_SCHEMES: readonly AppearanceScheme[] = [
  {
    id: 'navy',
    labelKey: 'connect.settings.appearance.brandNavy',
    seeds: completeRuntimeSeeds({ ...statusSeeds(), ...brandThemes.navy }),
  },
  {
    id: 'navy-manual',
    labelKey: 'connect.settings.appearance.brandNavyManual',
    seeds: completeRuntimeSeeds({
      ...statusSeeds(),
      brand: NAVY_MANUAL_SEED,
      neutral: brandThemes.navy.neutral,
      info: NAVY_MANUAL_SEED,
    }),
    tokens: manualBrandThemes['navy-manual'],
  },
  {
    id: 'petrol',
    labelKey: 'connect.settings.appearance.brandPetrol',
    seeds: completeRuntimeSeeds({
      ...statusSeeds(),
      brand: PETROL_SEEDS.brand,
      neutral: PETROL_SEEDS.neutral,
    }),
    overrides: {
      light: {
        'brand-bg': PETROL_SEEDS.brand,
        'nav-bg': PETROL_SEEDS.brand,
        link: PETROL_SEEDS.brand,
      },
    },
    type: { font: 'system', mono: 'system', scale: 1 },
    design: {
      surface: 'hairline',
      density: 'compact',
      actionlist: 'list',
      badges: 'pill',
      page: 'plain',
    },
  },
  {
    id: 'pink',
    labelKey: 'connect.settings.appearance.brandPink',
    seeds: completeRuntimeSeeds({
      ...statusSeeds(),
      brand: defaultSeeds.roles.brand,
      neutral: defaultSeeds.roles.neutral,
      info: defaultSeeds.roles.info,
    }),
  },
  {
    id: 'amber',
    labelKey: 'connect.settings.appearance.brandAmber',
    seeds: completeRuntimeSeeds({ ...statusSeeds(), ...brandThemes.amber }),
  },
  ...GALLERY_THEMES.map(galleryScheme),
];

export function isNamedBrand(id: string): id is NamedBrand {
  return (PLATFORM_APPEARANCE_SCHEME_IDS as readonly string[]).includes(id);
}

export function getAppearanceScheme(id: string): AppearanceScheme | undefined {
  return PLATFORM_APPEARANCE_SCHEMES.find((scheme) => scheme.id === id);
}
